import { useI18n } from "@wojo/localization";
import { StatusLabel, TaskLabel } from "@wojo/ui";
import { TripStatus } from "generated/graphql";
import React from "react";
import { ChecklistTaskLabel } from "../checklist-task-label";

export type TripCardStatusLabelProps = React.PropsWithChildren<{
    status: TripStatus;
    uncheckedItemsCount: {
        recommended: number;
        required: number;
    };
    hasSignedTerms: boolean;
    isPastTrip?: boolean;
    reviewInfo: {
        canBeReviewed: boolean;
        isReviewSubmitted: boolean;
    };
}>;

export const TripCardStatusLabel: React.FC<TripCardStatusLabelProps> = ({
    uncheckedItemsCount,
    status,
    hasSignedTerms,
    isPastTrip,
    reviewInfo: { canBeReviewed, isReviewSubmitted },
}) => {
    const l10n = useI18n("account").account;

    if (isPastTrip && canBeReviewed) {
        return (
            <StatusLabel
                status={isReviewSubmitted ? "success" : "info"}
                text={
                    isReviewSubmitted
                        ? l10n.tourReview.statusLabels.reviewEligibleSubmitted.l()
                        : l10n.tourReview.statusLabels.reviewEligibleNotSubmitted.l()
                }
            />
        );
    }

    if (!hasSignedTerms) {
        return (
            <TaskLabel
                status="alert"
                text={l10n.myTours.tripCard.status.signTerms.l()}
            />
        );
    }

    if (status === TripStatus.PendingRebooking) {
        return (
            <StatusLabel
                status="info"
                variant="primary"
                text={l10n.onHold.l()}
            />
        );
    }

    if (status === TripStatus.Requested) {
        return (
            <StatusLabel
                status="info"
                variant="primary"
                text={l10n.requested.l()}
            />
        );
    }

    if (
        uncheckedItemsCount.recommended > 0 ||
        uncheckedItemsCount.required > 0
    ) {
        return (
            <ChecklistTaskLabel
                recommended={uncheckedItemsCount.recommended}
                required={uncheckedItemsCount.required}
            />
        );
    }

    return null;
};
